/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Eye, EyeOff, Star, GripVertical, Save, X, Loader2, Film, Play } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";

const EMPTY = {
  full_name: "", designation: "", specialization: "", teaching_subject: "",
  experience_years: 0, qualification: "", bio: "", profile_image: "",
  banner_image: "", demo_lecture_url: "", priority: 100, is_featured: false,
  status: "active", rating: 4.9, review_count: 0,
  achievements_text: "",      // textarea, split by newline
  linkedin: "", youtube: "", instagram: "", twitter: "", website: "",
  admin_notes: "",
};

export default function FacultyAdminPanel() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);   // current editing faculty (or "new")
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/faculty");
      setList(data.items || []);
    } catch { toast.error("Failed to load faculty"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({ ...EMPTY });
  const openEdit = (f) => setEdit({
    ...EMPTY, ...f,
    achievements_text: (f.achievements || []).join("\n"),
    linkedin: f.social_links?.linkedin || "", youtube: f.social_links?.youtube || "",
    instagram: f.social_links?.instagram || "", twitter: f.social_links?.twitter || "",
    website: f.social_links?.website || "",
  });

  const save = async () => {
    if (!edit.full_name?.trim()) { toast.error("Full name is required"); return; }
    setSaving(true);
    const payload = {
      full_name: edit.full_name,
      designation: edit.designation,
      specialization: edit.specialization,
      teaching_subject: edit.teaching_subject,
      experience_years: Number(edit.experience_years) || 0,
      qualification: edit.qualification,
      bio: edit.bio,
      profile_image: edit.profile_image,
      banner_image: edit.banner_image,
      demo_lecture_url: edit.demo_lecture_url,
      priority: Number(edit.priority) || 100,
      is_featured: !!edit.is_featured,
      status: edit.status || "active",
      rating: Number(edit.rating) || 0,
      review_count: Number(edit.review_count) || 0,
      achievements: (edit.achievements_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
      social_links: {
        linkedin: edit.linkedin, youtube: edit.youtube, instagram: edit.instagram,
        twitter: edit.twitter, website: edit.website,
      },
      admin_notes: edit.admin_notes,
    };
    try {
      if (edit.id) {
        await api.patch(`/admin/faculty/${edit.id}`, payload);
        toast.success("Faculty updated");
      } else {
        await api.post("/admin/faculty", payload);
        toast.success("Faculty created");
      }
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.full_name}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/faculty/${f.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleVisibility = async (f, patch) => {
    try { await api.post(`/admin/faculty/${f.id}/visibility`, patch); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="faculty-admin-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs text-[var(--text-dim)]">{list.length} faculty total · {list.filter(f => f.is_featured).length} featured · {list.filter(f => f.status === "active").length} active</div>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-1.5 bg-[#FF0066] hover:bg-[#e6005c] text-white text-sm font-semibold rounded-lg px-3 py-1.5" data-testid="add-faculty-btn">
          <Plus className="w-3.5 h-3.5" /> Add Faculty
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-white/40">Loading…</div>
      ) : list.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-8 text-center text-white/50">
          No faculty yet. Click <b>Add Faculty</b> to create the first one.
        </div>
      ) : (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-4 py-2.5">Faculty</th>
                <th className="text-left px-4 py-2.5">Specialization</th>
                <th className="text-left px-4 py-2.5">Exp</th>
                <th className="text-left px-4 py-2.5">Priority</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {list.map((f) => (
                <tr key={f.id} className="hover:bg-white/[0.02]" data-testid={`faculty-row-${f.id}`}>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#FF0066]/15 overflow-hidden shrink-0">
                        {f.profile_image ? <img src={f.profile_image} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                      <div>
                        <div className="font-semibold text-white">{f.full_name}</div>
                        <div className="text-[11px] text-white/50">{f.designation || "—"}</div>
                      </div>
                      {f.is_featured && <Star className="w-3 h-3 text-amber-400 fill-current" />}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-white/65 max-w-[200px] truncate">{f.specialization}</td>
                  <td className="px-4 py-3 text-xs">{f.experience_years || 0}y</td>
                  <td className="px-4 py-3 text-xs">{f.priority}</td>
                  <td className="px-4 py-3">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold border ${f.status === "active" ? "bg-emerald-500/10 text-emerald-300 border-emerald-400/30" : "bg-white/5 text-white/40 border-white/10"}`}>{f.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => toggleVisibility(f, { is_featured: !f.is_featured })} className="w-7 h-7 rounded bg-amber-500/10 hover:bg-amber-500/20 border border-amber-400/30 inline-flex items-center justify-center" title={f.is_featured ? "Unfeature" : "Feature"}>
                        <Star className={`w-3.5 h-3.5 ${f.is_featured ? "text-amber-400 fill-current" : "text-amber-400/60"}`} />
                      </button>
                      <button onClick={() => toggleVisibility(f, { status: f.status === "active" ? "inactive" : "active" })} className="w-7 h-7 rounded bg-blue-500/10 hover:bg-blue-500/20 border border-blue-400/30 inline-flex items-center justify-center" title={f.status === "active" ? "Hide" : "Show"}>
                        {f.status === "active" ? <Eye className="w-3.5 h-3.5 text-blue-300" /> : <EyeOff className="w-3.5 h-3.5 text-blue-300" />}
                      </button>
                      <button onClick={() => openEdit(f)} className="w-7 h-7 rounded bg-white/5 hover:bg-white/10 border border-white/15 inline-flex items-center justify-center" title="Edit"><Edit3 className="w-3.5 h-3.5 text-white/70" /></button>
                      <button onClick={() => remove(f)} className="w-7 h-7 rounded bg-red-500/10 hover:bg-red-500/20 border border-red-400/30 inline-flex items-center justify-center" title="Delete"><Trash2 className="w-3.5 h-3.5 text-red-300" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 md:p-6" data-testid="faculty-edit-modal">
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-[#FF0066]/30 rounded-2xl shadow-2xl">
            <div className="sticky top-0 z-10 bg-[#0a0a0a] border-b border-white/10 px-5 py-3 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{edit.id ? "Edit Faculty" : "Add New Faculty"}</h3>
              <button onClick={() => setEdit(null)} className="text-white/50 hover:text-white p-1" aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-4">
              <Field label="Full name *">
                <input value={edit.full_name} onChange={(v) => setEdit({ ...edit, full_name: v.target.value })} className={INPUT} data-testid="f-name" />
              </Field>
              <Field label="Designation">
                <input value={edit.designation} onChange={(v) => setEdit({ ...edit, designation: v.target.value })} className={INPUT} placeholder="e.g. Founder & Lead Trainer" />
              </Field>
              <Field label="Specialization">
                <input value={edit.specialization} onChange={(v) => setEdit({ ...edit, specialization: v.target.value })} className={INPUT} placeholder="Digital Marketing, Meta Ads, Google Ads" />
              </Field>
              <Field label="Teaching subject">
                <input value={edit.teaching_subject} onChange={(v) => setEdit({ ...edit, teaching_subject: v.target.value })} className={INPUT} />
              </Field>
              <Field label="Experience (years)">
                <input type="number" value={edit.experience_years} onChange={(v) => setEdit({ ...edit, experience_years: v.target.value })} className={INPUT} />
              </Field>
              <Field label="Qualification">
                <input value={edit.qualification} onChange={(v) => setEdit({ ...edit, qualification: v.target.value })} className={INPUT} placeholder="MBA Digital Marketing" />
              </Field>
              <Field label="Profile photo (square recommended)" className="md:col-span-2">
                <div className="space-y-2">
                  <ImageUploader
                    value={edit.profile_image}
                    onChange={(url) => setEdit({ ...edit, profile_image: url })}
                    label="Profile photo"
                    aspect="1/1"
                    testId="f-profile-image"
                  />
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mt-2">Or paste image URL directly</div>
                  <input
                    value={edit.profile_image}
                    onChange={(v) => setEdit({ ...edit, profile_image: v.target.value })}
                    className={INPUT}
                    placeholder="https://… or /uploads/…"
                    data-testid="f-profile-image-url"
                  />
                </div>
              </Field>
              <Field label="Banner image (wide hero, optional)" className="md:col-span-2">
                <div className="space-y-2">
                  <ImageUploader
                    value={edit.banner_image}
                    onChange={(url) => setEdit({ ...edit, banner_image: url })}
                    label="Banner image"
                    aspect="16/9"
                    testId="f-banner-image"
                  />
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mt-2">Or paste image URL directly</div>
                  <input
                    value={edit.banner_image}
                    onChange={(v) => setEdit({ ...edit, banner_image: v.target.value })}
                    className={INPUT}
                    placeholder="https://… or /uploads/…"
                    data-testid="f-banner-image-url"
                  />
                </div>
              </Field>
              <Field label="Bio" className="md:col-span-2">
                <textarea value={edit.bio} onChange={(v) => setEdit({ ...edit, bio: v.target.value })} className={INPUT + " h-20"} placeholder="Short bio…" />
              </Field>
              <Field label="Achievements (one per line)" className="md:col-span-2">
                <textarea value={edit.achievements_text} onChange={(v) => setEdit({ ...edit, achievements_text: v.target.value })} className={INPUT + " h-24 font-mono text-xs"} placeholder={"Meta Certified\nGoogle Ads Certified\n10k+ students placed"} />
              </Field>
              <Field label="Demo lecture URL" className="md:col-span-2">
                <input value={edit.demo_lecture_url} onChange={(v) => setEdit({ ...edit, demo_lecture_url: v.target.value })} className={INPUT} placeholder="YouTube link" />
              </Field>
              {/* Social */}
              <Field label="LinkedIn">
                <input value={edit.linkedin} onChange={(v) => setEdit({ ...edit, linkedin: v.target.value })} className={INPUT} />
              </Field>
              <Field label="YouTube">
                <input value={edit.youtube} onChange={(v) => setEdit({ ...edit, youtube: v.target.value })} className={INPUT} />
              </Field>
              <Field label="Instagram">
                <input value={edit.instagram} onChange={(v) => setEdit({ ...edit, instagram: v.target.value })} className={INPUT} />
              </Field>
              <Field label="Twitter / X">
                <input value={edit.twitter} onChange={(v) => setEdit({ ...edit, twitter: v.target.value })} className={INPUT} />
              </Field>
              <Field label="Priority (lower = shown first)">
                <input type="number" value={edit.priority} onChange={(v) => setEdit({ ...edit, priority: v.target.value })} className={INPUT} />
              </Field>
              <Field label="Rating">
                <input type="number" step="0.1" min="0" max="5" value={edit.rating} onChange={(v) => setEdit({ ...edit, rating: v.target.value })} className={INPUT} />
              </Field>
              <Field label="Status">
                <select value={edit.status} onChange={(v) => setEdit({ ...edit, status: v.target.value })} className={INPUT}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="hidden">Hidden</option>
                </select>
              </Field>
              <Field label="Featured">
                <label className="inline-flex items-center gap-2 text-sm text-white/80 mt-2">
                  <input type="checkbox" checked={!!edit.is_featured} onChange={(v) => setEdit({ ...edit, is_featured: v.target.checked })} /> Show as featured on homepage
                </label>
              </Field>
              <Field label="Admin notes (private)" className="md:col-span-2">
                <textarea value={edit.admin_notes} onChange={(v) => setEdit({ ...edit, admin_notes: v.target.value })} className={INPUT + " h-16"} placeholder="Internal notes — never shown publicly" />
              </Field>
              {edit.id && (
                <ClipsManager
                  facultyId={edit.id}
                  clips={edit.clips || []}
                  onChange={(clips) => setEdit({ ...edit, clips })}
                />
              )}
              {!edit.id && (
                <div className="md:col-span-2 bg-amber-500/5 border border-amber-400/20 rounded-lg p-3 text-xs text-amber-200/80">
                  💡 Teaching clips ko save karne ke baad add kar sakte ho — pehle faculty profile create karein, fir edit me clips add karna.
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-white/10 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setEdit(null)} className="px-4 py-2 rounded-full text-sm text-white/70 hover:text-white">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-60 text-white text-sm font-semibold rounded-full px-4 py-2" data-testid="faculty-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const INPUT = "w-full bg-[#050505] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]";

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-wider text-white/50 font-bold mb-1">{label}</label>
      {children}
    </div>
  );
}

// =============================================================
// ClipsManager — inline CRUD for a faculty's short teaching clips
// =============================================================
function detectPlatform(url) {
  const u = (url || "").toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
  if (u.includes("instagram.com")) return "instagram";
  if (u.includes("vimeo.com")) return "vimeo";
  return "direct";
}

function ClipsManager({ facultyId, clips, onChange }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ url: "", title: "", priority: 100 });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!draft.url.trim()) { toast.error("Clip URL is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/faculty/${facultyId}/clips`, {
        url: draft.url.trim(),
        title: draft.title.trim(),
        priority: Number(draft.priority) || 100,
        platform: detectPlatform(draft.url),
      });
      onChange([...(clips || []), data]);
      setDraft({ url: "", title: "", priority: 100 });
      setAdding(false);
      toast.success("Clip added");
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  const remove = async (clipId) => {
    if (!window.confirm("Delete this teaching clip?")) return;
    try {
      await api.delete(`/admin/faculty/${facultyId}/clips/${clipId}`);
      onChange((clips || []).filter((c) => c.id !== clipId));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="md:col-span-2 rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3" data-testid="clips-manager">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-fuchsia-300 font-semibold inline-flex items-center gap-1.5">
          <Film className="w-3.5 h-3.5" /> Teaching Clips · {(clips || []).length}
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 text-xs bg-white/5 hover:bg-white/10 border border-white/15 rounded-lg px-2.5 py-1.5"
            data-testid="add-clip-btn"
            type="button"
          >
            <Plus className="w-3 h-3" /> Add clip
          </button>
        )}
      </div>

      {adding && (
        <div className="bg-[#050505] border border-white/10 rounded-lg p-3 mb-3 space-y-2" data-testid="clip-add-form">
          <input
            value={draft.url}
            onChange={(e) => setDraft({ ...draft, url: e.target.value })}
            className={INPUT}
            placeholder="YouTube / Instagram Reel / Vimeo / direct .mp4 URL"
            data-testid="clip-url-input"
          />
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={INPUT} placeholder="Title (optional, e.g. 'Meta Ads — Targeting basics')" data-testid="clip-title-input" />
            <input type="number" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} className={INPUT} placeholder="Priority (lower = first)" />
          </div>
          <div className="text-[10px] text-white/40">
            YouTube ke liye thumbnail auto-generate hoga. Instagram clips new tab mein khulenge.
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setAdding(false); setDraft({ url: "", title: "", priority: 100 }); }} className="text-xs text-white/60 px-3 py-1.5" type="button">Cancel</button>
            <button onClick={add} disabled={busy} className="bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-60 text-white text-xs font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1" data-testid="clip-save-btn" type="button">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save clip
            </button>
          </div>
        </div>
      )}

      {(clips || []).length === 0 && !adding ? (
        <div className="text-xs text-white/40 text-center py-4">No clips yet. Add YouTube links, Instagram reels, or direct video URLs.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {(clips || []).map((c) => (
            <div key={c.id} className="relative rounded-lg overflow-hidden border border-white/10 bg-[#050505]" data-testid={`clip-card-${c.id}`}>
              <div className="aspect-video bg-black relative">
                {c.thumbnail ? (
                  <img src={c.thumbnail} alt={c.title || "Clip"} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    <Play className="w-7 h-7" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <span className="absolute top-1 left-1 text-[9px] uppercase tracking-wider bg-black/60 px-1.5 py-0.5 rounded text-fuchsia-300">{c.platform}</span>
                <button
                  onClick={() => remove(c.id)}
                  className="absolute top-1 right-1 w-6 h-6 rounded bg-red-500/20 hover:bg-red-500/40 border border-red-400/30 inline-flex items-center justify-center"
                  type="button"
                  title="Delete clip"
                  data-testid={`clip-delete-${c.id}`}
                >
                  <Trash2 className="w-3 h-3 text-red-200" />
                </button>
              </div>
              <div className="p-2">
                <div className="text-[11px] font-semibold text-white truncate">{c.title || "Untitled clip"}</div>
                <div className="text-[10px] text-white/40 truncate">P{c.priority}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
